import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import type {
	singleStaticMacroQuery as SingleStaticMacroQueryType,
	singleStaticMacroQuery$variables,
} from './__generated__/singleStaticMacroQuery.graphql.ts';

const NIL_UUID = '00000000-0000-0000-0000-000000000000';

// Fetches fresh static HTML for a single macro via `confluence_staticMacro` (singular), added to
// the AGG schema in https://bitbucket.org/atlassian/cc-graphql/pull-requests/5371. This avoids
// re-fetching (or re-paginating) the full `confluence_staticMacros` connection used for the
// page's initial load — see ONECLOUD-20314.
//
// This operation is whitelisted for the View Page route (see
// `View_Page_Initial_Route_API_Whitelist.csv`) because it only ever fires in response to a user
// edit. When editing prose here, avoid the word "query" immediately followed by another word: the
// detector behind that whitelist scrapes operation names with a regex that doesn't distinguish
// comments from the tag below, and any stray match is read as an un-whitelisted operation.
const SingleStaticMacroQuery = graphql`
	query singleStaticMacroQuery(
		$contentId: ID!
		$cloudId: ID!
		$localId: String!
		$mode: MacroRendererMode
	) {
		confluence_staticMacro(
			contentId: $contentId
			cloudId: $cloudId
			localId: $localId
			mode: $mode
		) @optIn(to: ["AskInCcApiPlatformBeforeUsing"]) {
			macroId
			value
			error {
				code
			}
		}
	}
`;

export type SingleStaticMacroResult =
	| { value: string; errorCode?: undefined }
	| { value?: undefined; errorCode: string }
	| undefined;

/**
 * Fetches fresh server-rendered static HTML for a single Forge staticView macro, identified by
 * its `localId` — without re-fetching (or re-paginating) the full `confluence_staticMacros`
 * connection used for the page's initial load.
 *
 * Returns `{ value }` when static rendering succeeded, `{ errorCode }` when the backend returned
 * an error for this macro, or `undefined` if the macro couldn't be found at all (e.g. it no
 * longer exists on the page).
 */
export async function queryStaticMacro({
	contentId,
	cloudId,
	localId,
	mode,
}: {
	contentId: string;
	cloudId?: string | null;
	localId: string;
	mode?: singleStaticMacroQuery$variables['mode'];
}): Promise<SingleStaticMacroResult> {
	const { data, error } = await queryInterop<
		SingleStaticMacroQueryType['response'],
		SingleStaticMacroQueryType['variables']
	>({
		query: SingleStaticMacroQuery,
		variables: {
			contentId,
			cloudId: cloudId || NIL_UUID,
			localId,
			mode,
		},
		fetchPolicy: 'cache-and-network',
		context: { allowOnExternalPage: true },
	});

	if (error) {
		throw error;
	}

	const node = data?.confluence_staticMacro;
	if (!node) {
		return undefined;
	}
	if (node.value) {
		return { value: node.value };
	}
	if (node.error) {
		return { errorCode: node.error.code };
	}
	return undefined;
}
